import { Box, Flex, Grid, Text, FlexProps } from '@chakra-ui/react';
import { IconLock } from 'icons';
import { ReactNode } from 'react';

interface LevelBarProps extends FlexProps {
  title?: string;
  icon?: ReactNode;
  tunedUpIcon?: ReactNode;
  maxValue: number;
  currentValue: number;
  numberOfBars?: number;
  isLockedStat?: boolean;
  isTunedUp?: boolean;
}

export const LevelBar = ({
  title,
  icon,
  tunedUpIcon,
  maxValue,
  currentValue,
  numberOfBars = 6,
  isLockedStat = false,
  isTunedUp = false,
  ...flexProps
}: LevelBarProps) => {
  const percentage = (currentValue / maxValue) * 100;
  const filledBars = (percentage / 100) * numberOfBars;
  const fullBars = Math.floor(filledBars);
  const partialBarWidth = (filledBars - fullBars) * 100;

  const getBarColor = (value: number) => {
    if (isLockedStat) return 'rgba(255, 255, 255, 0.2)'
    if (value <= 33.34) return 'bloodMoon.100';
    if (value <= 66.67) return 'secondaryYellow.100';
    return 'baltic.100';
  };

  const getBackgroundColor = (value: number) => {
    if (isLockedStat) return 'rgba(232, 232, 232, 0.1)';
    if (value <= 33.34) return 'rgba(216, 56, 50, 0.15)';
    if (value <= 66.67) return 'rgba(255, 243, 140, 0.15)';
    return 'rgba(79, 197, 186, 0.15)';
  };

  const color = getBarColor(percentage);
  const backgroundColor = getBackgroundColor(percentage);

  const bars = Array(numberOfBars)
    .fill('')
    .map((_, index) => (
      <Box
        key={index}
        w="full"
        h="0.18rem"
        position="relative"
        shadow="dark-lg"
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="rgba(255, 255, 255, 0.2)"
          borderRadius="full"
        />
        {index < fullBars ? (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg={color}
            borderRadius="full"
          />
        ) : index === fullBars && partialBarWidth > 0 ? (
          <Box
            position="absolute"
            top={0}
            left={0}
            width={`${partialBarWidth}%`}
            bottom={0}
            bg={color}
            borderRadius="full"
          />
        ) : null}
      </Box>
    ));

  return (
    <Flex 
      gap={{ base: '0.25rem', md: '0.5rem' }} 
      alignItems="center" 
      w="full"
      px={{ base: '1', md: '2' }}
      py="1"
      {...flexProps}
      background={
        isTunedUp 
        ? 'linear-gradient(180deg, rgba(255, 255, 153, 0) 0%, rgba(255, 255, 153, 0.12) 100%)'
        : 'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.06) 100%)'
      }
      position="relative"
      _before={isTunedUp ? {
        content: '""',
        position: 'absolute',
        borderRadius: '0.25rem',
        inset: 0,
        background: 'linear-gradient(360deg, rgba(229, 186, 85, 0.6) 0%, rgba(229, 186, 85, 0) 100%)',
        zIndex: -1,
        padding: '2px',
        mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
        maskComposite: 'exclude',
      } : undefined}
      opacity={isLockedStat ? 0.6 : 1}
      borderRadius="0.25rem"
    >
      {icon && (
        <Box>
          {isLockedStat ? (
            <IconLock __css={{ path: { fill: 'white.80' } }} w={{ base: '1rem', md: '1.25rem' }} h={{ base: '1rem', md: '1.25rem' }} />
          ) : (
            isTunedUp ? tunedUpIcon : icon
          )}
        </Box>
      )}
      <Flex gap="0.25rem" color="white.100" flexDir="column" w="full">
        {title && (
          <Flex w="full" justifyContent="space-between" alignItems="center">
            <Text
              textTransform="capitalize"
              fontSize={{ base: '10px', md: '12px' }}
              lineHeight={{ base: '12px', md: '14px' }}
              noOfLines={1}
              fontWeight="500"
              color="white.80"
              flex="1"
            >
              {title}
            </Text>
          </Flex>
        )}
        <Grid
          w="full"
          gridTemplateColumns={`repeat(${numberOfBars}, 1fr)`}
          columnGap="3px"
          background={backgroundColor}
          borderRadius="0.25rem"
          p="0.18rem"
        >
          {bars}
        </Grid>
      </Flex>
    </Flex>
  );
};
